import React, { Component } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import MediaDisplay from '../../Common/MediaDisplay';
import { paragraph } from '../../../styling/mixins/typography';
import Heading from '../../Common/Heading';
import TrackedLink from '../../Common/Buttons/TrackedLink';
import DefaultButton from '../../Common/Buttons/DefaultButton';
import {
  MOBILE_END_QUERY,
  DESKTOP_START_QUERY,
  DESKTOP_MEDIUM_START_QUERY,
  DESKTOP_LARGE_START_QUERY,
} from '../../../styling/mixins/responsive';
import { BasisFont } from '../../../styling/mixins/typography';
import Assets from '../VibeDetailsPage/Assets';
import withTrackingPropsProvider from '../../../hocs/withTrackingPropsProvider';
import { PaddedCheckList } from './CheckList';
import MultiLineText from './MultiLineText';
import WindowHeader from '../../../assets/svg/WindowHeader';
import withClickTracking from '../../../tracking/withClickTracking';

const Container = styled.div`
  margin: 1rem 0 3rem;
`;

const TileContainer = styled.div`
  width: 100%;
  height: 100%;
  ${({ withBorderOnHover }) =>
    withBorderOnHover
      ? `&:hover { box-shadow: 0 4px 20px 0 rgba(51, 51, 51, 0.16); }`
      : null}
`;

const TrackedTile = withClickTracking(styled.a`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  margin: auto;
  width: 100%;
  padding-top: 2rem;

  ${({ isMobile }) =>
    isMobile
      ? `
          box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
          padding: 1rem;
          width: auto;
        `
      : null};
`);

const ProductName = styled(Heading.h3)`
  ${BasisFont};
  font-weight: 700;
  letter-spacing: -0.015em;

  @media (${MOBILE_END_QUERY}) {
    font-size: 1.25rem;
  }

  @media (${DESKTOP_MEDIUM_START_QUERY}) {
    font-size: 1.5rem;
  }

  @media (${DESKTOP_LARGE_START_QUERY}) {
    font-size: 2rem;
  }
`;

const ProductDescription = styled.div`
  ${paragraph};
  letter-spacing: -0.015em;
  margin: 0 auto 1.5rem;
  font-weight: 500;
  width: 80%;

  @media (${DESKTOP_MEDIUM_START_QUERY}) {
    line-height: 1.25em;
  }
`;

const Content = styled.div`
  width: 100%;
`;

const Media = styled.div`
  width: 80%;
  margin: 0 auto;
  /* box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15); */
`;

const CenteredWindowHeader = styled(WindowHeader)`
  margin: auto;
`;

const StyledMediaDisplay = styled(MediaDisplay)`
  width: 100%;
  margin: 0 auto;

  ${({ isMobile }) => (!isMobile ? `margin: 0 auto 2rem; ` : null)};
`;

const ButtonArea = styled.div`
  width: 100%;
  margin: 1rem 0 0.5rem;

  @media (${DESKTOP_START_QUERY}) {
    margin: 1rem 0 2rem;
  }
`;

const StyledTrackedLink = styled(TrackedLink)`
  font-size: 0.75rem;
  font-weight: 700;
`;

const AssetRow = styled.div`
  padding: 0 2rem;
`;

const colProps = {
  xs: 11,
  sm: 6,
};

const createComparisonTile = (dataSource, isMobile, withBorderOnHover) => {
  let buttonContents = null;
  if (isMobile && dataSource.mobileButton) {
    buttonContents = <StyledTrackedLink {...dataSource.mobileButton} />;
  } else if (!isMobile && dataSource.button) {
    buttonContents = <DefaultButton {...dataSource.button} />;
  }

  return (
    <TileContainer withBorderOnHover={withBorderOnHover}>
      <TrackedTile
        identifier={dataSource.identifier}
        href={
          dataSource.mobileButton ? dataSource.mobileButton.href : undefined
        }
        isMobile={isMobile}
      >
        <Content>
          {dataSource.media.type === 'video' ? (
            <Media>
              <CenteredWindowHeader width="100%" height="15px" />
              <StyledMediaDisplay
                media={dataSource.media}
                imageSize={dataSource.media.imageSize}
                isMobile={isMobile}
              />
            </Media>
          ) : (
            <StyledMediaDisplay
              media={dataSource.media}
              imageSize={dataSource.media.imageSize}
              isMobile={isMobile}
            />
          )}
          <ProductName>{dataSource.title}</ProductName>{' '}
          {dataSource.description && (
            <ProductDescription>
              <MultiLineText lines={dataSource.description} />
            </ProductDescription>
          )}
          <PaddedCheckList isMobile={isMobile} {...dataSource.checkList} />
        </Content>
        {buttonContents ? <ButtonArea>{buttonContents}</ButtonArea> : null}
      </TrackedTile>
    </TileContainer>
  );
};

class Comparison extends Component {
  static propTypes = {
    flyer: PropTypes.object,
    cards: PropTypes.object,
    isMobile: PropTypes.bool,
    withBorderOnHover: PropTypes.bool,
  };

  render() {
    const { cards, flyer, isMobile, withBorderOnHover } = this.props;

    const cardsTile = createComparisonTile(cards, isMobile, withBorderOnHover);
    const flyerTile = createComparisonTile(flyer, isMobile, withBorderOnHover);

    return (
      <Container>
        <AssetRow>
          <Assets
            assets={[cardsTile, flyerTile]}
            renderAsset={tile => tile}
            colProps={colProps}
            withBorder={true}
          />
        </AssetRow>
      </Container>
    );
  }
}

export default withTrackingPropsProvider(Comparison);
